/**
 * Module dependencies.
 */

import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';

/**
 * Types.
 */

export type PiBucketKind = 'cash' | 'crypto';

export type PiParticipant = {
  id: string;
  userId: number | null;
  email: string | null;
  name: string | null;
  weight: number;
  role: 'owner' | 'editor' | 'viewer';
  isSelf: boolean;
};

export type PiBucket = {
  id: string;
  name: string;
  kind: PiBucketKind;
  assetSymbol: string | null;
};

export type PiProject = {
  id: string;
  ownerId: number;
  isOwner: boolean;
  myRole: 'owner' | 'editor' | 'viewer' | null;
  name: string;
  description: string | null;
  currency: string;
  createdAt: string;
  updatedAt: string;
};

export type PiProjectListItem = PiProject & {
  participantCount: number;
  bucketCount: number;
};

export type PiProjectDetail = PiProject & {
  participants: PiParticipant[];
  buckets: PiBucket[];
};

/**
 * Query key prefix.
 */

const KEY = 'poolInvest';

/**
 * Shared invalidation of the whole section.
 */

function useInvalidate() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries([KEY]);
}

/**
 * List the projects the user can see.
 */

export function usePoolInvestProjects() {
  const client = useRequestProvider();

  return useQuery<PiProjectListItem[]>([KEY, 'projects'], {
    queryFn: async () => client.get(resolveAppUrl('poolInvestProjects')).then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * A single project with participants and buckets.
 */

export function usePoolInvestProject(id?: string) {
  const client = useRequestProvider();

  return useQuery<PiProjectDetail>([KEY, 'project', id], {
    enabled: !!id,
    queryFn: async () =>
      client.get(resolveAppUrl('poolInvestProjectById', { interpolations: { id } })).then(({ data }: any) => data?.data)
  });
}

/**
 * A participant's frozen holding valued at the current price.
 */

export type PiValuationHolding = { symbol: string; quantity: string; valueFiat: number };

export type PiValuationRow = {
  id: string;
  name: string | null;
  email: string | null;
  weight: number;
  active: boolean;
  investedFiat: number;
  feesFiat: number;
  currentValue: number;
  profitFiat: number;
  profitPct: number | null;
  holdings: PiValuationHolding[];
};

export type PiValuation = {
  currency: string;
  prices: Record<string, number>;
  participants: PiValuationRow[];
  totals: {
    investedFiat: number;
    feesFiat: number;
    currentValue: number;
    profitFiat: number;
    profitPct: number | null;
    holdings: Array<{ symbol: string; quantity: string; priceFiat: number; valueFiat: number }>;
  };
};

/**
 * Per-participant valuation of a pool at the current market price. Frozen shares
 * keep the historical value of members who dropped to weight 0.
 */

export function usePoolInvestValuation(projectId?: string) {
  const client = useRequestProvider();

  return useQuery<PiValuation>([KEY, 'valuation', projectId], {
    enabled: !!projectId,
    queryFn: async () =>
      client
        .get(resolveAppUrl('poolInvestProjectValuation', { interpolations: { projectId } }))
        .then(({ data }: any) => data?.data)
  });
}

/**
 * Participation over time — the effective split weight of each participant at
 * every date the weights change (rule boundaries), up to today.
 */

export type PiParticipationTimeline = {
  participants: Array<{ id: string; name: string | null }>;
  points: Array<{ date: string; weights: Record<string, number> }>;
};

export function usePoolInvestParticipationTimeline(projectId?: string) {
  const client = useRequestProvider();

  return useQuery<PiParticipationTimeline>([KEY, 'participationTimeline', projectId], {
    enabled: !!projectId,
    queryFn: async () =>
      client
        .get(resolveAppUrl('poolInvestParticipationTimeline', { interpolations: { projectId } }))
        .then(({ data }: any) => data?.data)
  });
}

/**
 * Create a project.
 */

export function useCreatePoolInvestProject() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (payload: { name: string; description?: string | null; currency?: string }) =>
      client.post(resolveAppUrl('poolInvestProjects'), payload).then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Delete a project (owner only).
 */

export function useDeletePoolInvestProject() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (id: string) =>
      client
        .delete(resolveAppUrl('poolInvestProjectById', { interpolations: { id } }))
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

// Participant management (add / edit / remove) is handled by the generic CRUD
// system — see pool-invest/project/users/form-config.tsx.

/**
 * A Crypto Gest transaction → pool allocation.
 */

export type PiCgLink = {
  sourceEntryId: string;
  projectId: string;
  projectName: string | null;
  currency: string | null;
  category: string | null;
  quantity: string | null;
  amountFiat: string;
};

/**
 * All Crypto Gest → pool links (to flag allocated transactions in the CG ledger).
 */

export function usePoolInvestCryptoGestLinks() {
  const client = useRequestProvider();

  return useQuery<PiCgLink[]>([KEY, 'cgLinks'], {
    queryFn: async () =>
      client.get(resolveAppUrl('poolInvestCryptoGestLinks')).then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * Allocate a Crypto Gest transaction into a pool (assign, or one leg of a split).
 */

export function useCreatePoolInvestFromCryptoGest() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({
      projectId,
      ...payload
    }: {
      projectId: string;
      sourceEntryId: string;
      bucketId: string;
      amountFiat: string;
      quantity?: string | null;
      unitPriceFiat?: string | null;
      direction?: 'in' | 'out';
      date?: string | null;
    }) =>
      client
        .post(resolveAppUrl('poolInvestFromCryptoGest', { interpolations: { projectId } }), payload)
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Undo all pool allocations of a Crypto Gest transaction.
 */

export function useRemovePoolInvestCryptoGestLinks() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (sourceEntryId: string) =>
      client
        .delete(resolveAppUrl('poolInvestCryptoGestLinkById', { interpolations: { sourceEntryId } }))
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * A bank transaction → pool allocation.
 */

export type PiBankLink = {
  sourceEntryId: string;
  projectId: string;
  projectName: string | null;
  category: string | null;
  direction: 'in' | 'out';
  amountFiat: string;
  feeFiat: string | null;
  feeNote: string | null;
};

/**
 * All bank → pool links (to flag allocated transactions in the bank ledger).
 */

export function usePoolInvestBankLinks() {
  const client = useRequestProvider();

  return useQuery<PiBankLink[]>([KEY, 'bankLinks'], {
    queryFn: async () => client.get(resolveAppUrl('poolInvestBankLinks')).then(({ data }: any) => data?.data ?? [])
  });
}

/**
 * Allocate a bank transaction into a pool (trace-only; the bank record stays).
 */

export function useCreatePoolInvestFromBank() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({
      projectId,
      ...payload
    }: {
      projectId: string;
      sourceEntryId: string;
      bucketId: string;
      amountFiat: string;
      direction?: 'in' | 'out';
      feeFiat?: string | null;
      feeNote?: string | null;
      note?: string | null;
      date?: string | null;
    }) =>
      client
        .post(resolveAppUrl('poolInvestFromBank', { interpolations: { projectId } }), payload)
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Undo all pool allocations of a bank transaction.
 */

export function useRemovePoolInvestBankLinks() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (sourceEntryId: string) =>
      client
        .delete(resolveAppUrl('poolInvestBankLinkById', { interpolations: { sourceEntryId } }))
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}
